// Permission utility functions
import { getCurrentUser, getCurrentRole } from './sessionManager';

/**
 * Get user's role from sessionStorage
 */
export const getUserRole = () => {
  try {
    return getCurrentRole();
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};

/**
 * Get user data from sessionStorage
 */
export const getUser = () => {
  try {
    return getCurrentUser();
  } catch (error) {
    console.error('Error getting user:', error);
    return null;
  }
};

/**
 * Check if user has permission for a specific module and action
 * @param {string} module - Module name (e.g., 'schedule', 'shipment', 'production')
 * @param {string} action - Action type ('view', 'create', 'edit', 'delete')
 * @returns {boolean} - Whether user has the permission
 */
export const hasPermission = (module, action) => {
  const role = getUserRole();
  
  if (!role || !role.permissions) {
    return false;
  }
  
  const modulePermissions = role.permissions[module];
  
  if (!modulePermissions) {
    return false;
  }
  
  return modulePermissions[action] === true;
};

/**
 * Check if user has any of the specified permissions
 * @param {Array} permissions - Array of {module, action} objects
 * @returns {boolean} - Whether user has at least one permission
 */
export const hasAnyPermission = (permissions) => {
  return permissions.some(perm => hasPermission(perm.module, perm.action));
};

/**
 * Check if user has all of the specified permissions
 * @param {Array} permissions - Array of {module, action} objects
 * @returns {boolean} - Whether user has all permissions
 */
export const hasAllPermissions = (permissions) => {
  return permissions.every(perm => hasPermission(perm.module, perm.action));
};

/**
 * Check if user is admin
 * @returns {boolean}
 */
export const isAdmin = () => {
  const role = getUserRole();
  return role && role.name === 'Admin';
};

/**
 * Get all permissions for current user
 * @returns {object} - Permissions object
 */
export const getAllPermissions = () => {
  const role = getUserRole();
  return role ? role.permissions : {};
};

/**
 * Check if module tab should be visible
 * User needs at least 'view' permission to see the tab
 * @param {string} module - Module name
 * @returns {boolean}
 */
export const canViewModule = (module) => {
  return hasPermission(module, 'view');
};
